import React from 'react';
import moment from 'moment';
import { GenericProductGetDTO } from 'svila-common';

import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import DatePickerInputComponent from '../../../../components/common/forms/DatePickerInput/DatePickerInputComponent';
import {
  API_URL,
  WAREHOUSE_MOVEMENT_TYPES,
  WAREHOUSE_MOVEMENT_STATUS_TYPES,
  WAREHOUSE_MOVEMENT_CONDITION_TYPES,
  DATE_FORMAT,
} from '../../../../utils/constants';
import LocationEditModal from '../../../../components/common/LocationEditModal';
import {
  constantsToAutocomplete,
  constantsToAutocompleteStatusTypes,
  convertToDropdown,
  getOrderCustomerName,
} from '../../../../utils/misc';
import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import ButtonGroupComponent from '../../../../components/common/forms/ButtonGroupComponent/ButtonGroupComponent';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import AutocompleteStaticComponent from '../../../../components/common/forms/Autcomplete/AutocompleteStaticComponent';
import styled from 'styled-components';
import InventoryBtnGroupComponent from './InventoryBtnGroupComponent';
import AutocomplitePaginatedComponent from '~/src/components/common/forms/Autcomplete/AutocomplitePaginatedComponent';

interface Props {
  generic: GenericProductGetDTO;
  formikProps: any;
  values?: any;
}

export default function InventoryAddEditForm({ formikProps, generic }: Props): JSX.Element {
  const {
    handleBlur,
    handleChange,
    initialValues,
    setFieldValue,
    values,
    touched,
    errors,
    handleSubmit,
    isSubmitting,
  } = formikProps;

  return (
    <form
      onSubmit={handleSubmit}
      autoComplete="off"
      style={{ background: '#F4F4F4', borderRadius: '10px', padding: '50px 100px' }}
    >
      <FormRow>
        <AutocompleteStaticComponent
          options={convertToDropdown(generic.products)}
          name="manufacturerId"
          onChange={setFieldValue}
          getOptionLabel={(option): string => `${option.label}`}
          initialValue={initialValues.manufacturerId}
          cols={4}
          errors={errors}
          touched={touched}
        />
        <AutocompleteComponent
          url={`${API_URL}/orders/search`}
          name="sellOrderId"
          label="Sell Order Id"
          initialValue={values.sellOrderId}
          onChange={setFieldValue}
          getOptionLabel={(option): string =>
            `${option.orderNumber} - ${moment(option.createdOn).format(DATE_FORMAT)} - ${getOrderCustomerName(
              option,
            )}`
          }
          queryFields={['orderNumber']}
          errorMessage={touched.sellOrderId ? errors.sellOrderId : null}
          cols={8}
        />
      </FormRow>
      <FormRow>
        <AutocompleteComponent
          url={`${API_URL}/contractors/suppliers/get`}
          name="supplierId"
          label="Add Supplier"
          initialValue={initialValues.supplierId}
          onChange={setFieldValue}
          getOptionLabel={(option): string => `${option.name}`}
          errorMessage={touched.supplierId ? errors.supplierId : null}
          cols={4}
        />
        <AutocompleteComponent
          key={values.supplierId}
          url={(values.supplierId && values.supplierId !== '')
            ? `${API_URL}/purchase-orders/search/${values.supplierId}`
            : `${API_URL}/purchase-orders/search`}
          name="purchaseOrderId"
          label="Purchase Order"
          initialValue={initialValues.purchaseOrderId}
          onChange={setFieldValue}
          getOptionLabel={(option): string =>
            `${option.orderNumber} - ${moment(option.createdOn).format(DATE_FORMAT)} - ${option.supplierName}`
          }
          errorMessage={touched.purchaseOrderId && errors.purchaseOrderId ? errors.purchaseOrderId : null}
          cols={8}
        />
      </FormRow>
      <FormRow>
        <TextInputComponent
          name="fullDuUnits"
          label="Full Du Units"
          value={values.fullDuUnits}
          onBlur={handleBlur}
          onChange={handleChange}
          cols={4}
          errors={errors}
          touched={touched}
        />
        <TextInputComponent
          name="singleDpUnits"
          label="Single Dp Units"
          value={values.singleDpUnits}
          onBlur={handleBlur}
          onChange={handleChange}
          cols={4}
          errors={errors}
          touched={touched}
        />
        <TextInputComponent
          name="singleCuUnits"
          label="Single Cu Units"
          value={values.singleCuUnits}
          onBlur={handleBlur}
          onChange={handleChange}
          cols={4}
          errors={errors}
          touched={touched}
        />
      </FormRow>

      <FormRow>
        <LocationEditModal values={values} setField={setFieldValue} touched={touched} errors={errors} />
      </FormRow>
      <FormRow>
        <TextInputComponent
          name="lot"
          label="Lot"
          value={values.lot}
          onBlur={handleBlur}
          onChange={handleChange}
          cols={4}
          errors={errors}
          touched={touched}
        />
        <DatePickerInputComponent
          name="expirationDate"
          label="Expiration date"
          onChange={(e): void => {
            setFieldValue('expirationDate', e.value);
          }}
          defaultValue={initialValues.expirationDate}
          touched={touched}
          errors={errors}
          cols={4}
          value={values.expirationDate}
        />
        <DatePickerInputComponent
          label="Production date"
          name="productionDate"
          onChange={(e): void => {
            setFieldValue('productionDate', e.value);
          }}
          defaultValue={initialValues.productionDate}
          touched={touched}
          errors={errors}
          cols={4}
          value={values.productionDate}
        />
        <InventoryBtnGroupComponent
          options={constantsToAutocomplete(WAREHOUSE_MOVEMENT_TYPES)}
          name="movementType"
          initialValue={initialValues.movementType}
          onChange={(v): void => {
            setFieldValue('movementType', v);
          }}
          cols={12}
          touched={touched}
          errors={errors}
        />
        <InventoryBtnGroupComponent
          options={constantsToAutocompleteStatusTypes(WAREHOUSE_MOVEMENT_STATUS_TYPES)}
          name="status"
          initialValue={initialValues.status}
          onChange={(v): void => {
            setFieldValue('status', v);
          }}
          cols={12}
          touched={touched}
          errors={errors}
        />
        <InventoryBtnGroupComponent
          options={constantsToAutocompleteStatusTypes(WAREHOUSE_MOVEMENT_CONDITION_TYPES)}
          name="condition"
          initialValue={initialValues.condition}
          onChange={(v): void => {
            setFieldValue('condition', v);
          }}
          cols={12}
          touched={touched}
          errors={errors}
        />

        <TextInputComponent
          name="accountedPrice"
          label="Accounted price"
          value={values.accountedPrice}
          onBlur={handleBlur}
          onChange={handleChange}
          cols={4}
          errors={errors}
          touched={touched}
        />
        <TextInputComponent
          name="accountedValue"
          label="Accounted value"
          value={values.accountedValue}
          onBlur={handleBlur}
          onChange={handleChange}
          cols={4}
          errors={errors}
          touched={touched}
        />
        <AutocompleteComponent
          url={`${API_URL}/contractors/warehouse-owners/get`}
          name="warehouseOwnerId"
          label="warehouseOwnerId"
          initialValue={values.warehouseOwnerId}
          onChange={setFieldValue}
          getOptionLabel={(option) => `${option.name}`}
          isMulti={false}
        />
        <AutocomplitePaginatedComponent
          url={`${API_URL}/warehouses/get`}
          name="warehouseId"
          label="warehouseId"
          initialValue={values.warehouseId}
          onChange={setFieldValue}
          getOptionLabel={(option) => `${option.name}`}
          isMulti={false}
          errorMessage={touched.warehouseId ? errors.warehouseId : null}
        />
      </FormRow>

      <FormRow>
        <PrimaryButton isSubmitting={isSubmitting} type="submit" title="Save" />
      </FormRow>
    </form>
  );
}

const AlignWrapperButtonGroupComponent = styled.div`
  div {
    width: 100%;
    label {
      width: 100%;
    }
  }
`;
